import { Moon, Sun, Flower, LucideIcon } from "lucide-react";
// New product images
import blueLotusFlowers from "@/assets/product-blue-lotus-flowers.jpg";
import yellowLotusFlowers from "@/assets/product-yellow-lotus-flowers.jpg";
import productFlatlay from "@/assets/product-flatlay-ingredients.jpg";
import driedFlowers from "@/assets/product-dried-flowers.jpg";
import teaLifestyle from "@/assets/product-tea-lifestyle.jpg";

export interface Product {
  id: number;
  name: string;
  slug: string;
  tagline: string;
  description: string;
  longDescription: string;
  price25g: string;
  price50g: string;
  bundlePrice: string;
  originalPrice: string;
  accentColor: string;
  borderColor: string;
  glowColor: string;
  icon: LucideIcon;
  bullets: string[];
  ingredients: string[];
  benefits: string[];
  productImage: string;
  lifestyleImage: string;
  detailImage: string;
  testimonials: {
    name: string;
    text: string;
    rating: number;
  }[];
  isPopular?: boolean;
  isBestDeal?: boolean;
  isAccessory?: boolean;
  usage?: string;
  contentInfo?: string;
  seoDescription?: string;
  dbId?: string; // Database UUID for live pricing
}

export const products: Product[] = [
  {
    id: 1,
    name: "Women's Balance Blend",
    slug: "womens-balance-blend",
    tagline: "Herstel je innerlijke flow",
    description: "Herstel je innerlijke flow. Deze blend ondersteunt hormonale balans en helpt je om rust en emotionele stabiliteit terug te vinden. Een thee afgestemd op de cyclus van de maan – jouw vrouwelijke ritueel.",
    longDescription: "Speciaal samengesteld voor vrouwen die hun natuurlijke balans willen herstellen. Deze luxueuze blend combineert de kracht van blauwe lotus met zorgvuldig geselecteerde kruiden die al eeuwenlang worden gebruikt om het vrouwelijke welzijn te ondersteunen. Perfect voor momenten van reflectie, meditatie en het eren van je natuurlijke cyclus.",
    price25g: "€11,11",
    price50g: "€22,22",
    bundlePrice: "€29,95",
    originalPrice: "€33,33",
    accentColor: "bg-purple-200",
    borderColor: "border-purple-300", 
    glowColor: "shadow-[0_0_25px_rgb(217,198,228,0.4)]",
    icon: Moon,
    bullets: [
      "Ondersteunt hormonale balans",
      "Helpt bij rust en emotionele stabiliteit",
      "Perfect afgestemd op je vrouwelijke ritueel",
      "Natuurlijke adaptogenen voor welzijn"
    ],
    ingredients: [
      "Frambozenblad",
      "Rode Klaver", 
      "Vrouwenmantel",
      "Citroenmelisse",
      "Roos",
      "Duizendblad"
    ],
    benefits: [
      "Ondersteunt natuurlijke hormonale cyclus",
      "Bevordert emotionele balans",
      "Helpt bij ontspanning en rust",
      "Ondersteunt vrouwelijk welzijn"
    ],
    productImage: blueLotusFlowers,
    lifestyleImage: teaLifestyle,
    detailImage: productFlatlay,
    testimonials: [
      {
        name: "Emma van der Berg",
        text: "Deze blend heeft mijn leven veranderd. Eindelijk voel ik me weer in balans en verbonden met mezelf.",
        rating: 5
      },
      {
        name: "Sophie Janssen", 
        text: "Perfect voor mijn avondritueel. De smaak is heerlijk en ik slaap veel beter.",
        rating: 5
      }
    ],
    isPopular: false
  },
  {
    id: 2,
    name: "Gut & Glow Blend", 
    slug: "gut-glow-blend",
    tagline: "Stralend van binnenuit",
    description: "Stralend van binnenuit. Deze blend ondersteunt je spijsvertering en bevordert een gezonde huid. Perfect als verlichtend ritueel na een maaltijd, voor een glow die van binnenuit begint.",
    longDescription: "Een harmonieuze blend die je spijsvertering ondersteunt en tegelijkertijd je natuurlijke schoonheid doet stralen. De zorgvuldig geselecteerde kruiden werken synergetisch samen om je darmen te voeden en je huid van binnenuit te laten gloeien. Ideaal voor na de maaltijd of als onderdeel van je beauty routine.",
    price25g: "€11,11", 
    price50g: "€22,22",
    bundlePrice: "€29,95",
    originalPrice: "€33,33",
    accentColor: "bg-orange-200",
    borderColor: "border-orange-300",
    glowColor: "shadow-[0_0_25px_rgb(246,213,142,0.4)]",
    icon: Sun,
    bullets: [
      "Ondersteunt de spijsvertering",
      "Bevordert een gezonde huid", 
      "Perfect als verlichtend ritueel na maaltijd",
      "Natuurlijke detox ondersteuning"
    ],
    ingredients: [
      "Venkel",
      "Paardenbloemwortel",
      "Pepermunt",
      "Brandnetel",
      "Goudsbloem", 
      "Rozenblaadje"
    ],
    benefits: [
      "Ondersteunt gezonde spijsvertering",
      "Bevordert natuurlijke detox",
      "Helpt bij een stralende huid",
      "Kalmeert maag en darmen"
    ],
    productImage: yellowLotusFlowers,
    lifestyleImage: teaLifestyle,
    detailImage: driedFlowers,
    testimonials: [
      {
        name: "Lisa de Vries",
        text: "Mijn huid is veel helderder geworden sinds ik deze thee dagelijks drink. En mijn spijsvertering is perfect!",
        rating: 5
      },
      {
        name: "Maria Bakker",
        text: "Deze blend smaakt heerlijk en ik voel me echt gezonder van binnen.",
        rating: 4
      }
    ],
    isPopular: true,
    isBestDeal: false
  },
  {
    id: 3,
    name: "Lunssence Signature Blend",
    slug: "signature-blend",
    tagline: "Kruideninfusie voor balans & rust",
    description: "Ontdek rust en balans. Deze harmonieuze blend combineert de kracht van Blauwe Lotus met ontspannende kruiden voor een moment van diepe sereniteit. Perfect voor het slapen of tijdens rustmomenten.",
    longDescription: "De Signature Blend is onze meest evenwichtige creatie – een perfecte harmonie van ontspannende en kalmerende kruiden. Blauwe Lotus vormt de basis, aangevuld met citroenmelisse voor mentale rust, kamille voor ontspanning, frambozenblad voor balans, lavendel voor diepe kalmte en rozenblaadjes voor een zachte, bloemige afsluiting. Elk ingrediënt is zorgvuldig geselecteerd om lichaam en geest in harmonie te brengen.",
    usage: "Voeg 1 theelepel (1-2g) kruiden toe aan 250 ml heet water. Laat 5-7 minuten trekken. Zeef en geniet rustig — het beste voor het slapen of tijdens rustmomenten.",
    contentInfo: "25g of 50g - premium gedroogde kruiden",
    seoDescription: "Lunssence Signature Blend - Kruideninfusie voor balans & rust. Blauwe Lotus thee met citroenmelisse, kamille, frambozenblad, lavendel en rozenblaadjes.",
    price25g: "€11,11",
    price50g: "€22,22",
    bundlePrice: "€29,95",
    originalPrice: "€33,33",
    accentColor: "bg-blue-200",
    borderColor: "border-blue-300",
    glowColor: "shadow-[0_0_25px_rgb(191,219,254,0.4)]",
    icon: Moon,
    bullets: [
      "Perfect voor avondritueel",
      "Bevordert rust en ontspanning",
      "Helpt bij slapen",
      "Natuurlijke balans voor lichaam en geest"
    ],
    ingredients: [
      "Blauwe Lotus",
      "Citroenmelisse",
      "Kamille",
      "Frambozenblad",
      "Lavendel",
      "Rozenblaadjes"
    ],
    benefits: [
      "Bevordert diepe ontspanning en rust",
      "Ondersteunt gezonde slaap",
      "Helpt bij stress en spanning",
      "Brengt lichaam en geest in balans"
    ],
    productImage: blueLotusFlowers,
    lifestyleImage: teaLifestyle,
    detailImage: productFlatlay,
    testimonials: [
      {
        name: "Sophie van Dam",
        text: "Deze blend is perfect voor mijn avondritueel. Ik val nu veel makkelijker in slaap.",
        rating: 5
      },
      {
        name: "Nina Vermeer",
        text: "De combinatie van kruiden is geweldig. Ik voel me echt ontspannen na een kopje.",
        rating: 5
      }
    ],
    isPopular: true
  },
  {
    id: 4,
    name: "Blauwe Lotus",
    slug: "blauwe-lotus", 
    tagline: "Sacred Flower of Calm",
    description: "Kruidenbloem voor ontspanning, balans & spirituele verbinding",
    longDescription: "Ervaar de magie van de Blauwe Lotus – het heilige symbool van rust, intuïtie en innerlijke wijsheid.\nDeze eeuwenoude bloem (Nymphaea caerulea) werd al in het oude Egypte gekoesterd om haar vermogen diepe ontspanning, helderheid en harmonie te brengen — lichaam, geest en ziel in balans.\n\nWanneer je Blauwe Lotus als thee drinkt, voel je hoe spanning langzaam verdwijnt.\nDe bloem opent het hart, kalmeert de geest en brengt een zachte, dromerige sereniteit.\nPerfect voor meditatie, creativiteit of als avondritueel voor het slapen.",
    usage: "Voeg 1 theelepel Blauwe Lotus toe aan 250 ml heet water.\nLaat 7–10 minuten trekken en geniet van de zachte, bloemige smaak.\nOok heerlijk te combineren met citroenmelisse of lavendel voor extra ontspanning.",
    contentInfo: "25 g of 50 g – handgedroogde bloembladeren van premium kwaliteit",
    seoDescription: "Blauwe Lotus thee – natuurlijke ontspanning, rust en spirituele balans. Helpt bij stress, slaap en bewustzijn. 100% puur, heilig kruid uit Egypte.",
    price25g: "€11,11",
    price50g: "€22,22", 
    bundlePrice: "€29,95",
    originalPrice: "€33,33",
    accentColor: "bg-gradient-gold",
    borderColor: "border-gold",
    glowColor: "shadow-gold",
    icon: Flower,
    bullets: [
      "Bevordert innerlijke rust en ontspanning",
      "Ondersteunt meditatie, intuïtie en bewustzijn",
      "Verlicht stress, spanning en onrustige gedachten",
      "Brengt balans bij hormonale en emotionele schommelingen",
      "100% natuurlijk, cafeïnevrij & vegan"
    ],
    ingredients: [
      "Pure Blauwe Lotus (Nymphaea caerulea) - handgedroogd"
    ],
    benefits: [
      "Bevordert innerlijke rust en ontspanning",
      "Ondersteunt meditatie, intuïtie en bewustzijn",
      "Verlicht stress, spanning en onrustige gedachten",
      "Brengt balans bij hormonale en emotionele schommelingen",
      "100% natuurlijk, cafeïnevrij & vegan"
    ],
    productImage: blueLotusFlowers,
    lifestyleImage: teaLifestyle,
    detailImage: productFlatlay,
    testimonials: [
      {
        name: "Anna Petersen",
        text: "Dit is echt magisch. Elke keer als ik deze thee drink voel ik me zo rustig en gecentreerd.",
        rating: 5
      },
      {
        name: "Julia Smit",
        text: "De smaak is ongelooflijk verfijnd. Perfect voor mijn dagelijkse meditatie.",
        rating: 5
      }
    ],
    isBestDeal: false
  },
  {
    id: 5,
    name: "Gele Lotus",
    slug: "gele-lotus",
    tagline: "Sacred Flower of Energy",
    description: "Kruidenbloem voor energie, focus & innerlijke helderheid",
    longDescription: "De Gele Lotus (Nymphaea lotus) is het symbool van vernieuwing, licht en innerlijke kracht.\nWaar de Blauwe Lotus ontspant, schenkt de Gele Lotus juist een zachte, opbeurende energie — helder van geest, licht van hart.\n\nAl sinds het oude Egypte werd deze bloem gebruikt om spirituele helderheid en bescherming te versterken.\nDe smaak is licht kruidig en aards, met een verfrissende toets.\nIdeaal voor momenten waarop je wilt opladen, maar liever geen cafeïne gebruikt.",
    usage: "Voeg 1 theelepel Gele Lotus toe aan 250 ml heet water.\nLaat 7–8 minuten trekken en geniet van de verkwikkende, zachte smaak.\nPerfect in de ochtend of overdag als natuurlijk energie-ritueel.",
    contentInfo: "25 g of 50 g – handgedroogde bloembladeren van premium kwaliteit",
    seoDescription: "Gele Lotus thee – kruidenthee voor natuurlijke energie, focus en mentale helderheid. Versterk je licht en kracht met deze heilige bloem.",
    price25g: "€11,11",
    price50g: "€22,22",
    bundlePrice: "€29,95",
    originalPrice: "€33,33",
    accentColor: "bg-yellow-200",
    borderColor: "border-yellow-300",
    glowColor: "shadow-[0_0_25px_rgb(253,224,71,0.4)]",
    icon: Sun,
    bullets: [
      "Bevordert focus en concentratie",
      "Geeft natuurlijke energie zonder onrust",
      "Helpt bij vermoeidheid en mentale spanning",
      "Versterkt helderheid, motivatie en zelfvertrouwen",
      "100% natuurlijk, cafeïnevrij & vegan"
    ],
    ingredients: [
      "Pure Gele Lotus (Nymphaea lotus) - handgedroogd"
    ],
    benefits: [
      "Bevordert focus en concentratie",
      "Geeft natuurlijke energie zonder onrust",
      "Helpt bij vermoeidheid en mentale spanning",
      "Versterkt helderheid, motivatie en zelfvertrouwen",
      "100% natuurlijk, cafeïnevrij & vegan"
    ],
    productImage: yellowLotusFlowers,
    lifestyleImage: teaLifestyle,
    detailImage: driedFlowers,
    testimonials: [
      {
        name: "Sophie Vermeer",
        text: "Perfect voor mijn ochtend ritueel. Ik voel me alert en helder zonder nerveus te worden.",
        rating: 5
      },
      {
        name: "Laura Bos",
        text: "De smaak is heerlijk en het geeft me precies die zachte energie boost die ik nodig heb.",
        rating: 5
      }
    ],
    isPopular: false
  },
  {
    id: 6,
    name: "Lunssence Thee Pot",
    slug: "thee-pot",
    tagline: "Premium Ceremoniële Thee Pot",
    description: "Verrijk jouw thee ritueel met onze handgemaakte ceremoniële thee pot. Perfect voor het bereiden van Lotus thee.",
    longDescription: "Deze elegante thee pot is speciaal ontworpen voor de optimale thee-ervaring. De porseleinen constructie behoudt de warmte perfect, terwijl het fijne filter zorgt voor een heldere infusie. Capaciteit: 600ml - perfect voor 2-3 kopjes.\n\nElke pot wordt met de hand afgewerkt en is een waar kunstwerk dat jouw thee ritueel naar een hoger niveau tilt.",
    price25g: "€17,77",
    price50g: "",
    bundlePrice: "",
    originalPrice: "€22,22",
    accentColor: "bg-amber-200",
    borderColor: "border-amber-300",
    glowColor: "shadow-[0_0_25px_rgb(251,191,36,0.4)]",
    icon: Flower,
    bullets: [
      "Handgemaakte ceremoniële thee pot",
      "Premium porselein voor optimale warmte",
      "Fijn geïntegreerd filter",
      "Capaciteit: 600ml (2-3 kopjes)",
      "Exclusief Lunssence design"
    ],
    ingredients: [],
    benefits: [
      "Perfecte thee extractie",
      "Behoudt warmte langer",
      "Elegante presentatie",
      "Duurzaam en tijdloos"
    ],
    productImage: productFlatlay,
    lifestyleImage: teaLifestyle,
    detailImage: driedFlowers,
    testimonials: [
      {
        name: "Mira van Dijk",
        text: "Deze thee pot maakt mijn thee ritueel compleet. De kwaliteit is geweldig!",
        rating: 5
      }
    ],
    isPopular: false,
    isAccessory: true
  }
];

export const bundleProduct = {
  id: 99,
  name: "Complete Ritueel Collectie",
  slug: "complete-ritueel-collectie", 
  tagline: "Alle 3 blends voor jouw perfecte ritueel",
  description: "Ontdek welke blend het beste bij jou past met onze complete collectie. Drie unieke ervaringen in één luxueuze set.",
  price: "€29,95",
  originalPrice: "€33,33", 
  savings: "€3,38",
  discount: "10%",
  products: products,
  isBestDeal: true
};

export const getProductBySlug = (slug: string): Product | undefined => {
  return products.find(product => product.slug === slug);
};